// Shared helpers + UI atoms for the Admin dashboard sections.
import React from "react";

export const currency = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(n || 0));

export const sumObjectValues = (obj = {}) =>
  Object.values(obj).reduce((a, b) => a + (Number(b) || 0), 0);

// brand palette — aligned to the reference dashboard (indigo primary).
// Used for Chart.js canvas colors (which can't read CSS variables) and
// for category accents. Neutral/grid tones are resolved per-theme by the
// useChartTheme() helper below.
export const brand = {
  primary: "#4f46e5", // indigo
  accent: "#4338ca",
  bgFrom: "#f4f6fa",
  bgTo: "#f4f6fa",
  textDark: "#0c1424",
  text: "#5b6b82",
  textMuted: "#90a0b5",
  border: "#e9edf3",
  success: "#15803d",
  info: "#1d4ed8",
  danger: "#be123c",
  // chart accent palette (varied for legibility across series)
  violet: "#7c3aed",
  sky: "#0ea5e9",
  amber: "#f59e0b",
  emerald: "#10b981",
  rose: "#f43f5e",
};

// Theme-aware tones for charts (grid lines + tick labels). Chart.js draws on
// canvas, so it cannot use CSS variables — we pick concrete values by theme.
export const chartTheme = (theme) =>
  theme === "dark"
    ? { tick: "#9aa8be", grid: "rgba(255,255,255,0.06)", legend: "#9aa8be" }
    : { tick: "#90a0b5", grid: "#eef2f7", legend: "#5b6b82" };

// Stat card (kept for backward compatibility; StatsCards now renders richer
// markup directly, but this helper stays available for reuse).
export function StatCard({ title, value, icon, sub, tone = "info" }) {
  const toneMap = {
    info: "var(--color-info-text)",
    success: "var(--color-success-text)",
    warning: "var(--color-warning-text)",
    danger: "var(--color-danger-text)",
    primary: "var(--color-primary)",
  };
  const toneBg = {
    info: "var(--color-info-bg)",
    success: "var(--color-success-bg)",
    warning: "var(--color-warning-bg)",
    danger: "var(--color-danger-bg)",
    primary: "var(--color-primary-weak)",
  };
  const color = toneMap[tone] || toneMap.info;
  const bg = toneBg[tone] || toneBg.info;

  return (
    <div className="card card--hover stat-card">
      <div className="stat-card__top">
        <p className="stat-card__title">{title}</p>
        <div className="stat-card__icon" style={{ color, background: bg }}>
          {icon}
        </div>
      </div>
      <div className="stat-card__row">
        <p className="stat-card__value">{value}</p>
      </div>
      {sub ? <p className="stat-card__note">{sub}</p> : null}
    </div>
  );
}

export function Card({ title, sub, right, children, className = "" }) {
  return (
    <div className={`card card--hover ${className}`}>
      <div className="card__header">
        <div>
          <h3 className="card__title">{title}</h3>
          {sub ? <p className="card__sub">{sub}</p> : null}
        </div>
        {right}
      </div>
      <div className="card__body">{children}</div>
    </div>
  );
}

// Status tone -> token pair (themes automatically in dark mode).
const PILL_TONES = {
  default: { bg: "var(--color-surface-2)", color: "var(--color-text-secondary)" },
  pending: { bg: "var(--color-warning-bg)", color: "var(--color-warning-text)" },
  sales_executive_assigned: {
    bg: "var(--color-info-bg)",
    color: "var(--color-info-text)",
  },
  design_received: { bg: "var(--color-info-bg)", color: "var(--color-info-text)" },
  quotation_received: {
    bg: "var(--color-success-bg)",
    color: "var(--color-success-text)",
  },
  order_tracking: {
    bg: "var(--color-primary-weak)",
    color: "var(--color-primary)",
  },
  order_completed: {
    bg: "var(--color-success-bg)",
    color: "var(--color-success-text)",
  },
};

export function Pill({ children, tone = "default" }) {
  const style = PILL_TONES[tone] || PILL_TONES.default;
  return (
    <span className="pill" style={{ background: style.bg, color: style.color }}>
      {children}
    </span>
  );
}

// Map status to pill tone
export const statusTone = (s) =>
  ({
    pending: "pending",
    sales_executive_assigned: "sales_executive_assigned",
    design_received: "design_received",
    quotation_received: "quotation_received",
    order_tracking: "order_tracking",
    order_completed: "order_completed",
  }[s] || "default");
